import { useMemo, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Combobox } from '../ui/combobox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useDimensionsList } from '../../hooks/useDimension';
import type { DashboardFilterOption } from '../../services/dashboard.service';

/**
 * Adds a filter to the dashboard's filter bar.
 *
 * Dimension filters only: it is the only type the control actually renders, so
 * offering the rest would be offering something that draws "Unsupported filter
 * type".
 */
export function AddDashboardFilter({
  onAdd,
}: {
  onAdd: (filter: DashboardFilterOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dimensionId, setDimensionId] = useState<string>('');
  const [label, setLabel] = useState('');
  const [allowMultiple, setAllowMultiple] = useState(false);

  // Only once the popover is open: a dashboard in edit mode should not pay for
  // a 500-row dimension fetch nobody asked for.
  const { data, isLoading } = useDimensionsList({ pageSize: 500, enabled: open });

  const options = useMemo(
    () =>
      (data?.records || [])
        .map((d) => ({ value: d.id, label: d.name || d.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [data],
  );

  const chosen = options.find((o) => o.value === dimensionId);

  const reset = () => {
    setDimensionId('');
    setLabel('');
    setAllowMultiple(false);
  };

  const handleAdd = () => {
    if (!dimensionId) return;
    onAdd({
      // Local id so React keys and later edits have something stable to hold.
      // The server round-trips it, including through git.
      id: `filter-${Date.now()}`,
      filter_type: 'DIMENSION',
      dimension_id: dimensionId,
      comparator: 'IN',
      allow_multiple: allowMultiple,
      // Falling back to the dimension's own name means the common case needs no
      // typing at all.
      input_label: label.trim() || chosen?.label || undefined,
    });
    reset();
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid="add-dashboard-filter">
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Add filter
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-4 space-y-4">
        <div className="space-y-2">
          <Label>Dimension</Label>
          <Combobox
            options={options}
            value={dimensionId}
            onValueChange={setDimensionId}
            placeholder={isLoading ? 'Loading dimensions...' : 'Select a dimension...'}
            emptyText="No dimensions found"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="bar-filter-label">Title</Label>
          <Input
            id="bar-filter-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={chosen?.label || 'Defaults to the dimension name'}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="bar-filter-multiple">Allow multiple selections</Label>
          <Switch id="bar-filter-multiple" checked={allowMultiple} onCheckedChange={setAllowMultiple} />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleAdd} disabled={!dimensionId}>
            Add filter
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
